/**
 * Why a load failed, said plainly on the loading scene: which step was running (a required step's
 * failure comes wrapped as a `StepError`, loading/loader.ts), whether it was a download (worth
 * trying again) or the graphics, and the technical line (the file, the status) as the details.
 */
import { AssetHttpError, AssetIntegrityError } from '../cache/assets';
import { failedUrl, mayHealOnce, reloadFresh } from '../cache/staleModules';
import type { LoadingScene } from './scene';

/** A required step of a load failed: `cause` is what it threw. */
export class StepError extends Error {
  constructor(
    readonly step: string,
    /** The step's line on the scene (Thai). */
    readonly label: string,
    override readonly cause: unknown,
  ) {
    super(`${step}: ${messageOf(cause)}`);
  }
}

const messageOf = (e: unknown): string => (e instanceof Error ? e.message : String(e));

/** The error under any `StepError` wrapping. */
export function rootCause(error: unknown): unknown {
  let e = error;
  while (e instanceof StepError) e = e.cause;
  return e;
}

/**
 * A module of the game's code that the browser could not load (Chrome, Safari and Firefox word it
 * differently): a new version replaced it, the network failed, or, on the dev server, the browser's
 * cached copy of a prebundled dependency asks for a chunk that has been rebuilt.
 */
export function isModuleLoadFailure(error: unknown): boolean {
  return /dynamically imported module|importing a module script failed|error loading dynamically imported module|module script/i.test(
    messageOf(rootCause(error)),
  );
}

export interface LoadFailure {
  /** A download that failed (the code or a file): trying again may work. Otherwise the graphics. */
  download: boolean;
  /** The line of the step that was running (Thai, without the trailing ellipsis), if known. */
  step: string | null;
  /** The step and what went wrong, for the scene's details line. */
  detail: string;
}

/** Most of the technical line shown: enough for the file's URL and the status. */
const DETAIL_MAX = 240;

export function loadFailure(error: unknown): LoadFailure {
  const cause = rootCause(error);
  const msg = messageOf(cause);
  const download =
    cause instanceof AssetHttpError ||
    cause instanceof AssetIntegrityError ||
    /fetch|import|module|network/i.test(msg);
  const step = error instanceof StepError ? error.label.replace(/[….\s]+$/, '') : null;
  const tech = msg.length > DETAIL_MAX ? `${msg.slice(0, DETAIL_MAX - 1)}…` : msg;
  return { download, step, detail: step ? `ขั้นตอน: ${step} · ${tech}` : tech };
}

/** What the player is told (Thai), by where the load stopped. */
const SAID = {
  download: 'ดาวน์โหลดข้อมูลเกมไม่สำเร็จ ตรวจสอบการเชื่อมต่ออินเทอร์เน็ตแล้วลองใหม่',
  graphics: 'เปิดเกมไม่สำเร็จ กรุณาตรวจว่าเบราว์เซอร์รองรับ WebGL และเปิดการเร่งกราฟิก',
  boot: 'เปิดเกมไม่สำเร็จ กรุณาลองใหม่',
  enter: 'เข้าสู่ยุทธภพไม่สำเร็จ กรุณาลองใหม่',
} as const;

/**
 * Shows a failed load on the scene: opening the game (`boot`) or entering the world (`enter`).
 * A part of the code that could not be loaded heals itself once (the cached code refreshed and
 * the page reloaded, cache/staleModules.ts); otherwise the scene says what failed, with the step
 * and the file as details, and "try again" refreshes the cached code before reloading.
 */
export function showLoadFailure(scene: LoadingScene, error: unknown, during: 'boot' | 'enter'): void {
  const f = loadFailure(error);
  const url = failedUrl(messageOf(rootCause(error)));
  const extra = url ? [url] : [];
  if (isModuleLoadFailure(error) && mayHealOnce()) {
    scene.step('กำลังโหลดเกมใหม่…');
    void reloadFresh(extra);
    return;
  }
  const retry = () => void reloadFresh(extra);
  if (f.download) return scene.fail(SAID.download, retry, f.detail);
  if (during === 'enter') return scene.fail(SAID.enter, retry, f.detail);
  const gpu = (error instanceof StepError && error.step === 'gpu') || /webgl|gpu/i.test(f.detail);
  if (gpu) return scene.fatal(SAID.graphics, f.detail);
  scene.fail(SAID.boot, retry, f.detail);
}
